import React, {
  Fragment,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';

import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from 'react-google-recaptcha-v3';

import { CountryCode } from '@crehana/ts-types';

import { Me } from '@crehana/web/dist/ReactContexts/AuthContext';

import { getQueryParam } from '@crehana/utils';

import { InitialStateResult } from 'Jsx/Utils/getBaseInitialState';
import { useAmplitude, getSourceEventParams } from 'Jsx/Analytics/Amplitude';

import { UserType } from 'Jsx/Types/UserType';

import Checkout from './Checkout';
import PromotionExpired from './pages/CheckoutCallCenter/PromotionExpired';
import { ProductData, ShoppingCartItem } from './types';
import formatMembershipTag from './utils/formatMembershipTag';

interface ExtraState {
  amplitudeVariant: string;
}

export interface AppProps {
  productData: ProductData;
  countryPrefix: CountryCode;
  shoppingCartItems?: ShoppingCartItem[];
  amplitudePageName?: string;
  user: Me | UserType | null;
  flags: { useNewCreditCardFeature: boolean };
}

const App: React.FC<InitialStateResult<AppProps & ExtraState>> = props => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [recaptchaToken, setRecaptchaToken] = useState<string>('');

  const { logEvent } = useAmplitude();
  const {
    productData: { callCenter },
    productData,
    amplitudePageName,
    user,
    flags,
    amplitudeVariant,
  } = props;

  const isGuestCheckout = !user;
  const isCallCenter = callCenter?.token && !callCenter?.hasExpired;

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      console.log('Execute recaptcha not yet available');
      return;
    }

    const token = await executeRecaptcha('checkout');

    setRecaptchaToken(token);
  }, [executeRecaptcha]);

  useEffect(() => {
    handleReCaptchaVerify();
  }, [handleReCaptchaVerify]);

  useEffect(() => {
    logEvent({
      event: 'Render Checkout B2C',
      data: {
        ...getSourceEventParams(),
        'Item ID': productData.itemId,
        'Item Type': productData.itemType,
        'Page Name': amplitudePageName || 'Checkout',
        'Checkout type': 'URL',
        'is logged': !isGuestCheckout,
        // @ts-expect-error
        Variant: amplitudeVariant,
      },
    });
  }, [amplitudeVariant]);

  const appProps = {
    ...props,
    user,
    isGuestCheckout,
    recaptchaToken,
    isCallCenter,
    flags,
  };

  return (
    <Fragment>
      {callCenter?.hasExpired ? (
        <PromotionExpired callCenter={productData.callCenter} />
      ) : (
        <Checkout {...appProps} />
      )}
    </Fragment>
  );
};

const CheckoutWithoutCaptcha: React.FC<
  InitialStateResult<AppProps & ExtraState>
> = props => {
  const { logEvent } = useAmplitude();
  const {
    productData: { callCenter },
    productData,
    amplitudePageName,
    user,
    amplitudeVariant,
  } = props;

  const isGuestCheckout = !user;
  const isCallCenter = callCenter?.token && !callCenter?.hasExpired;

  useEffect(() => {
    logEvent({
      event: 'Render Checkout B2C',
      data: {
        ...getSourceEventParams(),
        'Item ID': productData.itemId,
        'Item Type': productData.itemType,
        'Page Name': amplitudePageName || 'Checkout',
        'Checkout type': 'URL',
        'is logged': !isGuestCheckout,
        // @ts-expect-error
        'Membership Tag': formatMembershipTag(productData?.membershipTag),
        Variant: amplitudeVariant,
      },
    });
  }, [amplitudeVariant]);

  const appProps = {
    ...props,
    user,
    isGuestCheckout,
    recaptchaToken: '',
    isCallCenter,
  };

  return (
    <Fragment>
      {callCenter?.hasExpired ? (
        <PromotionExpired callCenter={productData.callCenter} />
      ) : (
        <Checkout {...appProps} />
      )}
    </Fragment>
  );
};

// Dummy comment to deploy

// @ts-ignore
const CheckoutWrapper = props => {
  const amplitudeVariant = useMemo(() => {
    return String(getQueryParam('source_detail')).replace('-', ' ');
  }, []);

  if (window.RECAPTCHA_PUBLIC_KEY)
    return (
      <GoogleReCaptchaProvider
        reCaptchaKey={window.RECAPTCHA_PUBLIC_KEY}
        scriptProps={{
          async: true, // optional, default to false,
          defer: true, // optional, default to false
          appendTo: 'head', // optional, default to "head", can be "head" or "body",
          nonce: undefined, // optional, default undefined
        }}
      >
        <App {...props} amplitudeVariant={amplitudeVariant} />
      </GoogleReCaptchaProvider>
    );

  // If the recaptcha key is not available we do not wrap the Checkout
  return (
    <CheckoutWithoutCaptcha {...props} amplitudeVariant={amplitudeVariant} />
  );
};

export default CheckoutWrapper;
